import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import { BlockUI } from 'primereact/blockui';
import { Menubar } from 'primereact/menubar';
import PerformanceCheckReport from '../../components/PerformanceCheck/PerformanceCheckReport';
import { ServiceProvider } from '../../services';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useReactToPrint } from 'react-to-print';
import { Repair } from '../../services/PerformanceCheckService';

const performanceCheckService = ServiceProvider.performanceCheck;

const formatTSRepairHistory = (data) => {
  return data.map((work) => {
    // 중복 제거를 위해 Set 사용
    const uniqueItems = new Set();

    // 모든 작업 항목 수집
    work.workItems.forEach((item) => {
      uniqueItems.add(item.partDeatilName);
    });

    // 시작일 포맷팅
    const inDay = dayjs(work.workRequestDate).format('YYYY-MM-DD');
    const items = Array.from(uniqueItems).sort();

    return {
      inDay,
      items,
    };
  });
};

const formatNiceDNRRepairHistory = (data) => {
  // 시작날짜별로 아이템을 그룹화하는 객체 생성
  const groupedByDate = data.reduce((acc, repair) => {
    if (!acc[repair.inDay]) {
      acc[repair.inDay] = [];
    }
    acc[repair.inDay].push(repair.workName);

    return acc;
  }, {});

  // 그룹화된 데이터를 원하는 형식의 배열로 변환
  return Object.entries(groupedByDate).map(([inDay, items]) => {
    return {
      inDay,
      items,
    };
  });
};

const getNiceDNRData = async ({ lpns, vin, vinRemain, regdate }) => {
  if (!lpns || !vin || !regdate) {
    return null;
  }

  try {
    const combinedVIN = vin + (vinRemain || '');
    const data = await Repair.getCenterCommercial(lpns, combinedVIN, regdate);
    const result = formatNiceDNRRepairHistory(data);
    return result;
  } catch (error) {
    console.error('NICEDNR 카센터 조회 실패:', error);

    // 400 에러일 경우 데이터 없는 경우라 에러 띄우지 않음
    if (error.response?.status === 400) {
      return [];
    }

    window.cerp.dialog.error(
      'NICEDNR 카센터 정비이력 조회 실패',
      `[${error.response?.data?.error?.code}] ${error.response?.data?.error?.message}`
    );
    return [];
  }
};

const getScopedPapers = (root, scope) => {
  if (!root) {
    return [];
  }

  const allPapers = Array.from(root.querySelectorAll('.paper'));
  if (!scope) {
    return allPapers;
  }

  if (scope === 'wheel') {
    return allPapers.filter(
      (paper) =>
        paper.classList.contains('wheel-check-vehicle-info-paper') ||
        paper.classList.contains('wheel-restoration-paper') ||
        paper.classList.contains('wheel-restoration-photo-paper')
    );
  }

  if (scope === 'warranty') {
    return allPapers.filter((paper) =>
      paper.classList.contains('wheel-premium-warranty-paper')
    );
  }

  return allPapers;
};

const buildPrintRoot = (root, scope) => {
  if (!root) {
    return null;
  }

  if (!scope) {
    return root;
  }

  const scopedPapers = getScopedPapers(root, scope);
  if (!scopedPapers.length) {
    return null;
  }

  const printRoot = document.createElement('div');
  printRoot.className = 'pcr';

  scopedPapers.forEach((paper) => {
    const container = paper.closest('.paper_container');
    printRoot.appendChild((container || paper).cloneNode(true));
  });

  return printRoot;
};

const PerformanceCheckViewer = ({
  match: {
    params: { statementNumber, dataId, scope: scopeParam },
  },
}) => {
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search || ''),
    [location.search]
  );
  const rawScope = scopeParam || searchParams.get('scope');
  const reportScope =
    rawScope === 'wheel' || rawScope === 'warranty' ? rawScope : null;
  const autoPrint = searchParams.get('autoPrint') === '1';
  const viewerTitle =
    reportScope === 'wheel'
      ? '휠복원 점검지'
      : reportScope === 'warranty'
        ? '휠 프리미엄 보증서'
        : '휠복원점검기록부';
  const pdfFileName =
    reportScope === 'wheel'
      ? `휠복원점검지_${statementNumber}.pdf`
      : reportScope === 'warranty'
        ? `휠프리미엄보증서_${statementNumber}.pdf`
        : `휠복원점검기록부_${statementNumber}.pdf`;

  const reportRef = useRef(null);
  const reportCaptureRef = useRef(null);
  const autoPrintTriggeredRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [checkData, setCheckData] = useState(null);
  const [addData, setAddData] = useState([]);
  const [workDetails, setWorkDetails] = useState([]);
  const [repairDetails, setRepairDetails] = useState([]);
  const [checkPhotos, setCheckPhotos] = useState(null);
  const [data, setData] = useState({
    paymentInformation: null,
    association: null,
    inspector: null,
    vehicleInformation: null,
    customerCompany: null,
  });

  const [errorData, setErrorData] = useState(null);
  const [reportCaptureData, setReportCaptureData] = useState([]);

  const handlePrint = useReactToPrint({
    content: () => {
      const root = reportRef.current;
      const printRoot = buildPrintRoot(root, reportScope);
      if (!printRoot) {
        return false;
      }

      const papers = getScopedPapers(printRoot, reportScope);
      if (papers.length === 0) {
        return false;
      }

      for (let i = 0; i < papers.length; i++) {
        const pageNumberEl = document.createElement('div');
        pageNumberEl.innerText = `Page ${i + 1} of ${papers.length}`;
        pageNumberEl.style.position = 'absolute';
        pageNumberEl.style.bottom = '20px';
        pageNumberEl.style.left = '0';
        pageNumberEl.style.width = '100%';
        pageNumberEl.style.textAlign = 'center';
        pageNumberEl.style.fontSize = '10px';
        pageNumberEl.style.zIndex = '99';
        pageNumberEl.classList.add('print-page-number');
        papers[i].appendChild(pageNumberEl);
      }

      return printRoot;
    },
    onBeforePrint: async () => {
      setLoading(true);
    },
    onAfterPrint: () => {
      const watermarks = reportRef.current.querySelectorAll('.cerp-watermark');
      _.map(watermarks, (watermark) => watermark.remove());

      const parts = reportRef.current.querySelectorAll('.cerp-pp');
      _.map(parts, (part) => (part.style.fontSize = '1.25rem'));

      // 프린트 후 페이지 번호 제거
      const pageNumbers = reportRef.current.querySelectorAll('.page-number');
      _.map(pageNumbers, (pageNumber) => pageNumber.remove());

      setLoading(false);
    },
  });

  const handlePDF = async () => {
    setLoading(true);

    // 화면에서 이미 로드된 .paper를 직접 캡처한다.
    // cloneNode 분리 DOM은 이미지 재로딩 + imageTimeout:0 조합으로 무한 대기할 수 있다.
    const papers = getScopedPapers(reportRef.current, reportScope);
    if (!papers.length) {
      setLoading(false);
      return false;
    }

    const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const pageImages = [];
    const reportPDF = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    try {
      for (let i = 0; i < papers.length; i++) {
        const page = papers[i];
        const watermark = buildWatermark(now);
        page.appendChild(watermark);

        try {
          const canvas = await html2canvas(page, {
            scale: 2,
            logging: false,
            allowTaint: false,
            useCORS: true,
            imageTimeout: 15000,
            backgroundColor: '#ffffff',
          });

          const pageImage = canvas.toDataURL('image/png');
          pageImages.push(pageImage);
        } finally {
          if (watermark.parentNode === page) {
            page.removeChild(watermark);
          }
        }
      }

      for (let i = 0; i < pageImages.length; i++) {
        reportPDF.addImage(
          pageImages[i],
          'PNG',
          0,
          0,
          210,
          297,
          `PAGE-${i}`,
          'NONE',
          0
        );
        if (i < pageImages.length - 1) reportPDF.addPage();
      }

      reportPDF.save(pdfFileName);
    } catch (error) {
      console.error('PDF 생성 실패:', error);
      if (window.cerp && window.cerp.dialog) {
        window.cerp.dialog.error(
          'PDF 다운로드 실패',
          '페이지 캡처 중 오류가 발생했습니다. 이미지를 확인 후 다시 시도해 주세요.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const buildWatermark = (now) => {
    const watermark = document.createElement('div');
    _.map(
      _.split(
        'cerp-watermark absolute top-50 left-0 right-0 opacity-40 text-gray-500 font-bold p-4rem text-center text-4xl origin-center',
        ' '
      ),
      (cls) => watermark.classList.add(cls)
    );
    watermark.style.transform = 'rotate(-30deg)';
    const text = document.createTextNode(`출력일시 : ${now}`);
    watermark.appendChild(text);

    return watermark;
  };

  useEffect(() => {
    setLoading(true);

    (async () => {
      const { data, error } = await performanceCheckService.getData(
        // statementNumber
        dataId
      );

      if (data) {
        const {
          performanceCheckData: {
            hRecheck: hRecheckRaw,
            checkData: checkDataRaw,
            addData: addDataRaw,
            paymentInformation: paymentInformationRaw,
            association: associationRaw,
            inspector: inspectorRaw,
            customerCompany: customerCompanyRaw,
            customer: customerRaw,
            vehicleInformation: vehicleInformationRaw,
          },
          performanceCheckPhotos,
          workDetails,
        } = data;

        const hRecheck = hRecheckRaw;
        const checkData = JSON.parse(checkDataRaw);
        const addData = JSON.parse(addDataRaw);
        const paymentInformation = JSON.parse(paymentInformationRaw);
        const association = JSON.parse(associationRaw);
        const inspector = JSON.parse(inspectorRaw);
        const customerCompany = JSON.parse(customerCompanyRaw);
        const customer = JSON.parse(customerRaw);
        const vehicleInformation = JSON.parse(vehicleInformationRaw);

        const repairDetails = await getNiceDNRData({
          lpns: _.get(vehicleInformation, 'VHRNO'),
          vin: _.get(vehicleInformation, 'VIN'),
          vinRemain: _.get(vehicleInformation, 'vinRemain'),
          regdate: _.get(vehicleInformation, 'FRST_REGIST_DE'),
        });

        const formattedWorkDetail = formatTSRepairHistory(workDetails);
        setRepairDetails(repairDetails);
        setCheckData(checkData);
        setAddData(addData);
        setWorkDetails(formattedWorkDetail);
        setCheckPhotos(performanceCheckPhotos.slice().reverse());
        setData((ps) => ({
          ...ps,
          hRecheck,
          paymentInformation,
          association,
          inspector,
          vehicleInformation,
          customerCompany,
          customer,
        }));
      }

      if (error) {
        window.cerp.dialog.error(
          '존재하지 않는 점검번호 입니다.',
          `[${statementNumber}]`
        );
        setErrorData(error);
      }
    })();

    setLoading(false);
  }, [dataId, statementNumber]);

  useEffect(() => {
    if (!autoPrint || !checkData || autoPrintTriggeredRef.current) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const tryAutoPrint = () => {
      if (cancelled || autoPrintTriggeredRef.current) {
        return;
      }

      const scopedPapers = getScopedPapers(reportRef.current, reportScope);
      const isReady = !reportScope || scopedPapers.length > 0;

      if (isReady || attempts >= 30) {
        autoPrintTriggeredRef.current = true;
        handlePrint();
        return;
      }

      attempts += 1;
      setTimeout(tryAutoPrint, 200);
    };

    const timer = setTimeout(tryAutoPrint, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [autoPrint, checkData, handlePrint, reportScope]);

  return (
    <div className="pcr_wrapper">
      <BlockUI
        fullScreen
        blocked={loading}
        template={
          loading && (
            <i className="pi pi-spin pi-spinner text-white-alpha-90 font-bold text-6xl" />
          )
        }
      >
        <Menubar
          className="sticky top-0 z-5"
          model={[
            {
              label: '출력하기',
              icon: 'pi pi-fw pi-print',
              command: handlePrint,
            },
            {
              label: 'PDF 다운로드',
              icon: 'pi pi-fw pi-download',
              command: handlePDF,
            },
          ]}
          start={
            <label className="text-2xl font-semibold mr-2">{viewerTitle}</label>
          }
        />
        <div className="flex flex-row align-items-center justify-content-center py-3">
          {checkData && (
            <PerformanceCheckReport
              ref={reportRef}
              data={checkData}
              addData={addData}
              workDetails={workDetails}
              repairDetails={repairDetails}
              photos={checkPhotos}
              hRecheck={_.get(data, 'hRecheck')}
              recheck={_.get(data, 'recheck')}
              hWarranty={_.get(data, 'hWarranty')}
              payment={_.get(data, 'paymentInformation')}
              association={_.get(data, 'association')}
              inspector={_.get(data, 'inspector')}
              customerCompany={_.get(data, 'customerCompany')}
              customer={_.get(data, 'customer')}
              vehicleInformation={_.get(data, 'vehicleInformation')}
              reportScope={reportScope}
            />
          )}
        </div>
      </BlockUI>
      {/*{_.map(reportCaptureData, data => (<img src={data} style={{width: '210mm'}} />))}*/}
    </div>
  );
};

export default PerformanceCheckViewer;
